import {
  BookOutlined,
  CompassOutlined,
  LockOutlined,
  MailOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { usePublicContact } from '../api/hooks';
import { apiBaseUrl } from '../config';
import { useSiteContext } from '../context/SiteContext';
import { useHelp } from '../help/HelpCenter';

/**
 * The help menu of the header: report a problem to the vendor's support with the context of the
 * page already in the message, write to the contact address, open the API documentation. The
 * addresses come from the server, so a deployment without them shows only the documentation.
 */
export function HelpMenu() {
  const { t } = useTranslation();
  const { me, site } = useSiteContext();
  const location = useLocation();
  const contact = usePublicContact();
  const help = useHelp();
  const support = contact.data?.support ?? null;
  const general = contact.data?.contact ?? null;
  const reportHref = () => {
    const subject = t('help.reportSubject', { site: site?.name ?? '—' });
    const body = [
      t('help.reportBodyIntro'),
      '',
      '',
      '---',
      `${t('help.reportTenant')}: ${me?.tenant.name ?? '—'}`,
      `${t('help.reportSite')}: ${site?.name ?? '—'}`,
      `${t('help.reportUser')}: ${me?.principal.email ?? '—'}`,
      `${t('help.reportPage')}: ${window.location.origin}${location.pathname}${location.search}`,
      `${t('help.reportTime')}: ${new Date().toISOString()}`,
      `${t('help.reportBrowser')}: ${navigator.userAgent}`,
    ].join('\n');
    return `mailto:${support}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const items = [
    // the help of this page and the guided tours (FR-TRV-30), inside the portal
    ...(help
      ? [
          {
            key: 'page-help',
            icon: <ReadOutlined />,
            label: t('help.pageHelp'),
            onClick: help.openHelp,
          },
          ...(help.pageTour
            ? [
                {
                  key: 'page-tour',
                  icon: <PlayCircleOutlined />,
                  label: t('help.tourOfPage'),
                  onClick: () => help.startTour(help.pageTour!),
                },
              ]
            : []),
          {
            key: 'portal-tour',
            icon: <CompassOutlined />,
            label: t('help.tourOfPortal'),
            onClick: () => help.startTour('discover'),
          },
          { type: 'divider' as const },
        ]
      : []),
    ...(support
      ? [
          {
            key: 'report',
            icon: <WarningOutlined />,
            label: (
              <a href={reportHref()} target="_blank" rel="noreferrer">
                {t('help.report')}
              </a>
            ),
          },
        ]
      : []),
    ...(general
      ? [
          {
            key: 'contact',
            icon: <MailOutlined />,
            label: (
              <a href={`mailto:${general}`} target="_blank" rel="noreferrer">
                {t('help.contact', { email: general })}
              </a>
            ),
          },
        ]
      : []),
    ...(support || general ? [{ type: 'divider' as const }] : []),
    {
      key: 'privacy',
      icon: <LockOutlined />,
      label: (
        <a href="/privacy" target="_blank" rel="noreferrer">
          {t('privacy.link')}
        </a>
      ),
    },
    {
      key: 'docs',
      icon: <BookOutlined />,
      label: (
        <a href={`${apiBaseUrl}/docs`} target="_blank" rel="noreferrer">
          {t('help.apiDocs')}
        </a>
      ),
    },
  ];
  return (
    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
      <Tooltip title={t('help.title')}>
        <Button
          data-tour="help"
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
          aria-label={t('help.title')}
          style={{ height: 44, width: 44 }}
        />
      </Tooltip>
    </Dropdown>
  );
}
